import {useDerivedObservable, useObservableValueRef, useObservedState} from '@github-ui/react-observable'
import type {PropsWithChildren} from 'react'
import {createContext, use, useEffect, useLayoutEffect, useReducer} from 'react'

import {EntitlementProvider} from '../components/quota/EntitlementContext'
import {CopilotSpaceSelectionProvider} from '../contexts/CopilotSpaceSelectionContext'
import {useRestoredChatState, useSaveChatState} from '../hooks/use-restore-chat-state'
import {makeRepositoryReference} from './copilot-chat-helpers'
import type {CopilotChatAction, CopilotChatState} from './copilot-chat-reducer'
import {copilotChatReducer} from './copilot-chat-reducer'
import {constructMessagesHierarchy} from './copilot-chat-subthreading-helpers'
import type {
  CopilotChatAgent,
  CopilotChatMessage,
  CopilotChatMode,
  CopilotChatModel,
  CopilotChatOrg,
  CopilotChatPayload,
  CopilotChatReference,
  CopilotChatRepo,
  CopilotChatThread,
  CopilotSpace,
  RepositoryReference,
} from './copilot-chat-types'
import {copilotLocalStorage} from './copilot-local-storage'
import {CopilotChatManagerProvider} from './CopilotChatManagerContext'
import {getDefaultModel} from './models'
import {ObservableChatStateProvider, useObservableChatStateContext} from './ObservableChatContext'

const CopilotChatDispatchContext = createContext<React.Dispatch<CopilotChatAction> | null>(null)

const emptySet: ReadonlySet<CopilotChatReference> = new Set<CopilotChatReference>()

export interface CopilotChatProviderProps {
  topic?: CopilotChatRepo
  threadId: string | null
  workerPath: string
  refs: CopilotChatReference[]
  selectedReference?: CopilotChatReference | null
  mode: CopilotChatMode
  ssoOrganizations: CopilotChatOrg[]
  chatIsOpen?: boolean
  chatIsVisible?: boolean
  chatVisibleSettingPath?: string
  agents?: CopilotChatAgent[]
  customCopilots?: CopilotSpace[]
  messages?: CopilotChatMessage[]
  testReducerState?: CopilotChatState
  realIp?: string
  currentView?: 'thread' | 'list'
  copilotChatPayload: CopilotChatPayload
}

export function CopilotChatProvider({
  children,
  topic,
  workerPath,
  threadId,
  refs,
  selectedReference,
  mode,
  ssoOrganizations,
  chatIsOpen,
  chatIsVisible,
  chatVisibleSettingPath,
  agents,
  customCopilots,
  messages,
  testReducerState,
  realIp,
  currentView,
  copilotChatPayload,
}: PropsWithChildren<CopilotChatProviderProps>) {
  const restoredChatState = useRestoredChatState(threadId, mode)
  let restoredMessages = restoredChatState.restoredMessages
  const restoredThreadTitle = restoredChatState.restoredThreadTitle

  if (restoredMessages) {
    restoredMessages = constructMessagesHierarchy(restoredMessages)
  }

  const topicAsReference: RepositoryReference | undefined = topic ? makeRepositoryReference(topic) : undefined
  const defaultModel: CopilotChatModel = getDefaultModel(null, null)

  const initialState: CopilotChatState = testReducerState || {
    threadSelectedAt: new Date(),
    threadsLoading: {state: 'pending', error: null},
    messagesLoading: {state: 'pending', error: null},
    messagesRestored: !!restoredMessages,
    slashCommandLoading: {state: 'pending', error: null},
    showTopicPicker: mode !== 'immersive' && (!threadId || mode === 'assistive') && !topic,
    topicLoading: {state: 'pending', error: null},
    threads: new Map<string, CopilotChatThread>(),
    restoredThreadTitle,
    model: defaultModel,
    availableModels: [defaultModel],
    modelsLoading: {state: 'pending', error: null},
    messages: messages ?? restoredMessages ?? [],
    streamingMessage: null,
    selectedThreadID: threadId,
    currentTopic: undefined,
    chatIsOpen: Boolean(chatIsOpen),
    isWaitingOnCopilot: false,
    isWaitingOnAttachment: false,
    attachmentProcessingType: null,
    currentUserLogin: copilotChatPayload.currentUserLogin,
    apiUrl: copilotChatPayload.apiURL,
    currentReferences: topicAsReference ? [topicAsReference, ...refs] : refs,
    implicitReferences: emptySet,
    findFileWorkerPath: workerPath,
    currentView: currentView || 'thread',
    selectedReference: selectedReference ?? null,
    mode,
    currentRepository: topic,
    ssoOrganizations,
    context: undefined,
    customInstructions: copilotChatPayload.customInstructions,
    chatIsVisible,
    chatVisibleSettingPath,
    renderBetaLabel: copilotChatPayload.renderBetaLabel,
    topRepositoriesCache: undefined,
    agentsPath: copilotChatPayload.agentsPath,
    customCopilotsEnabled: copilotChatPayload.customCopilotsEnabled ?? false,
    customCopilotsIndividualAccessEnabled: copilotChatPayload.customCopilotsIndividualAccessEnabled ?? true,
    optedInToPreviewFeatures: copilotChatPayload.optedInToPreviewFeatures,
    optedInToUserFeedback: copilotChatPayload.optedInToUserFeedback,
    agents,
    customCopilots,
    reviewLab: copilotChatPayload.reviewLab,
    repoCustomInstructionsEnabled: copilotLocalStorage.getRepoCustomInstructionsState(),
    ambientError: undefined,
    personalInstructions: copilotChatPayload.personalInstructions ?? null,
    threadHasNewMessages: false,
    sharedThreadsLoading: {state: 'pending', error: null},
    skillOptions: {
      deepCodeSearch: false,
    },
    wrapCodeLines: copilotLocalStorage.getWrapCodeLines(),
    showCopilotSpacesMcpBanner: copilotChatPayload.showCopilotSpacesMcpBanner ?? false,
    showAgentCapabilitiesTooltip: copilotChatPayload.showAgentCapabilitiesTooltip ?? false,
    sharedThreadChannel: copilotChatPayload.sharedThreadChannel,
    autoSubmit: copilotChatPayload.autoSubmit ?? false,
    plan: copilotChatPayload.plan,
    agentQuotaSnapshot: copilotChatPayload.agentQuotaSnapshot,
    hasCEorCBAccess: copilotChatPayload.hasCEorCBAccess,
    agentsEnabled: copilotChatPayload.agentsEnabled,
    showDecoupledPRModeBanner: copilotChatPayload.showDecoupledPRModeBanner,
  }

  const [state, dispatch] = useReducer(copilotChatReducer, initialState)
  useSaveChatState(state.messages, state.selectedThreadID, state.threads, state.mode)

  useEffect(
    function saveCurrentReferences() {
      if (!state.chatIsOpen) return

      const referencesWithoutCurrentRepo = state.currentReferences.filter(
        r => r.type !== 'repository' || r.id !== state.currentRepository?.id,
      )
      copilotLocalStorage.setCurrentReferences(state.selectedThreadID, referencesWithoutCurrentRepo)
    },
    [state.selectedThreadID, state.currentReferences, state.currentRepository, state.chatIsOpen],
  )

  return (
    <EntitlementProvider
      initialLicenseType={copilotChatPayload.licenseType}
      initialPlan={copilotChatPayload.plan}
      initialQuotas={copilotChatPayload.quotas}
      hasCEorCBAccess={copilotChatPayload.hasCEorCBAccess}
      initialCanUpgradePlan={copilotChatPayload.canUpgradePlan}
    >
      <ChatStateProvider state={state}>
        <CopilotChatDispatchContext value={dispatch}>
          <CopilotChatManagerProvider
            apiURL={copilotChatPayload.apiURL}
            apiVersion={copilotChatPayload.apiVersion}
            state={state}
            dispatch={dispatch}
            ssoOrganizations={ssoOrganizations}
            realIp={realIp}
            hasCEorCBAccess={copilotChatPayload.hasCEorCBAccess}
          >
            <CopilotSpaceSelectionProvider selectedThreadId={state.selectedThreadID}>
              {children}
            </CopilotSpaceSelectionProvider>
          </CopilotChatManagerProvider>
        </CopilotChatDispatchContext>
      </ChatStateProvider>
    </EntitlementProvider>
  )
}

const ChatStateContext = createContext<CopilotChatState | null>(null)

export function ChatStateProvider({state, children}: PropsWithChildren<{state: CopilotChatState}>) {
  // A constant reference to an Observable
  const [stateObservable, setStateObservable] = useObservableValueRef(state)

  // When the state updates, update the value of the observable
  useLayoutEffect(() => {
    if (state !== stateObservable.current.value) {
      setStateObservable(state)
    }
  }, [state, stateObservable, setStateObservable])

  return (
    <ObservableChatStateProvider value={stateObservable}>
      <ChatStateContext value={state}>{children}</ChatStateContext>
    </ObservableChatStateProvider>
  )
}

/**
 * Returns the current version of the chat state.
 * This causes the component to re-render any time there is an update to any part of the state.
 * Our goal is to deprecate and remove this hook.
 *
 * @example
 * ```ts
 * const {messages, customInstructions} = useChatState()
 * ```
 */
export function useChatState(): CopilotChatState {
  const chatState = use(ChatStateContext)
  if (!chatState) throw new Error('useChatState can only be used inside a CopilotChatProvider')
  return chatState
}

/**
 * Returns the current version of the chat state or undefined if not within a CopilotChatProvider.
 * This is a safe version of useChatState that won't throw an error when used outside
 * of a chat context. Useful for components that may or may not be rendered within
 * Copilot Chat.
 *
 * @example
 * ```ts
 * const chatState = useSafeChatState()
 * const isImmersiveChat = chatState?.mode === 'immersive'
 * ```
 */
export function useSafeChatState(): CopilotChatState | undefined {
  const chatState = use(ChatStateContext)
  return chatState ?? undefined
}

/**
 * Returns the result of the lens function applied to the chat state.
 * This causes the component to re-render whenever the given function results in a new value.
 * The lens function must always be pure.
 *
 * @example
 * ```ts
 * const hasMessages = useChatStateLens(s => s.messages.length > 0)
 * ```
 */
export function useChatStateLens<T>(lens: (state: CopilotChatState) => T): T {
  const o = useObservableChatStateContext()
  if (!o || !o.current) throw new Error('useChatStateLens can only be used inside a CopilotChatProvider')
  const valueObservable = useDerivedObservable(o.current, lens)
  return useObservedState(valueObservable)
}

/**
 * Returns the current value of `state[key]` on the `CopilotChatState`.
 * This causes the component to re-render whenever that property has a new value.
 *
 * @example
 * ```ts
 * const model = useChatStateValue('model')
 * ```
 */
export function useChatStateValue<T extends keyof CopilotChatState>(key: T): CopilotChatState[T] {
  return useChatStateLens(s => s[key])
}

/**
 * Returns a subset of the `CopilotChatState` containing only the specified keys.
 * This causes the component to re-render whenever a property corresponding to one of those keys has a new value.
 *
 * @example
 * ```ts
 * const {model, availableModels} = useChatStateValues('model', 'availableModels')
 * ```
 */
export function useChatStateValues<TKey extends keyof CopilotChatState = never>(
  ...keys: TKey[]
): Pick<CopilotChatState, TKey> {
  return useChatStateLens(s => getSubsetFromState(s, keys))
}

function getSubsetFromState<TObject extends object, TKey extends keyof TObject>(
  state: TObject,
  keys: TKey[],
): Pick<TObject, TKey> {
  const out = {} as unknown as Pick<TObject, TKey>
  for (const key of keys) {
    out[key] = state[key]
  }
  return out
}

export function useChatDispatch(): React.Dispatch<CopilotChatAction> {
  const dispatch = use(CopilotChatDispatchContext)
  if (!dispatch) throw new Error('useChatDispatch can only be used inside a CopilotChatProvider')
  return dispatch
}
